import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type Reply = {
  text: string;
  action?: { label: string; path: string };
};

type Message = {
  id: string;
  role: "user" | "assistant";
  text: string;
  action?: { label: string; path: string };
};

type Chat = {
  id: string;
  title: string;
  messages: Message[];
};

const PROMPTS = [
  "Why aren't my auto follow-ups sending?",
  "How do I set up a Smart Trigger?",
  "Import my leads from a CSV file",
  "Connect a new WhatsApp account",
];

function replyFor(question: string): Reply {
  const text = question.toLowerCase();
  if (text.includes("follow-up") || text.includes("follow up") || text.includes("auto")) {
    return {
      text: "Open Inbox and select the lead. AI auto-reply is a switch on that lead. WhatsApp plain text only sends within 24 hours of the customer's last message. After that, the composer asks for an approved template. On Instagram, Human Agent covers the window after 24 hours, up to 7 days.",
      action: { label: "Open Inbox", path: "/events/conversations" },
    };
  }
  if (text.includes("pending") || text.includes("instagram") || text.includes("call")) {
    return {
      text: "WhatsApp follow-ups can be sent by AI, so they are not on your task list. Instagram replies and phone calls are waiting for a person. Open Pending tasks, then mark a row done after you reply or call.",
      action: { label: "Open Pending tasks", path: "/events/pending-tasks" },
    };
  }
  if (text.includes("trigger")) {
    return {
      text: "Smart Triggers is one rule: a condition and one action. Add a rule, then pick a keyword, time in a stage, a stage change, or a logged call. The stages in that list are the ones saved for this business.",
      action: { label: "Open Smart Triggers", path: "/events/smart-triggers" },
    };
  }
  if (text.includes("csv") || text.includes("import")) {
    return {
      text: "There is no CSV import on this account yet. Leads already in WhatsApp or Instagram show in CRM, grouped by the stages you set for this business.",
      action: { label: "Open CRM", path: "/events/crm" },
    };
  }
  if (text.includes("whatsapp") || text.includes("connect") || text.includes("account")) {
    return {
      text: "The WhatsApp number is the one connected in Settings. Inbox shows it as Connected to. If nothing is connected, the chat list says no WhatsApp number is connected for this business.",
      action: { label: "Open Settings", path: "/events/user-settings" },
    };
  }
  if (text.includes("lead recovery") || text.includes("question")) {
    return {
      text: "Lead recovery asks four plain questions: how long you wait for a first message, how many extra messages you send if they go quiet, how many you send before you stop, and what percent of 30-day-quiet customers you message again. Your number goes under You. With FrontDesk appears after you type it.",
      action: { label: "Open Lead recovery", path: "/events/lead-recovery" },
    };
  }
  return {
    text: "I can help with follow-ups, Smart Triggers, leads in CRM, and the connected WhatsApp number. Pick a suggestion, or ask about one of those.",
  };
}

export default function AskPanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-ask", onOpen);
    return () => window.removeEventListener("open-ask", onOpen);
  }, []);
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");

  const active = chats.find((chat) => chat.id === activeId) || null;
  const visibleChats = useMemo(
    () => chats.filter((chat) => chat.title.toLowerCase().includes(search.trim().toLowerCase())),
    [chats, search]
  );

  const startChat = (question?: string) => {
    const id = Math.random().toString(36).slice(2, 10);
    const messages: Message[] = [];
    if (question) {
      const reply = replyFor(question);
      messages.push(
        { id: `${id}-q`, role: "user", text: question },
        { id: `${id}-a`, role: "assistant", text: reply.text, action: reply.action }
      );
    }
    const chat: Chat = { id, title: question || "New chat", messages };
    setChats((current) => [chat, ...current]);
    setActiveId(id);
    setDraft("");
  };

  const send = (text: string) => {
    const question = text.trim();
    if (!question) return;
    const reply = replyFor(question);
    if (!active) {
      startChat(question);
      return;
    }
    setChats((current) =>
      current.map((chat) =>
        chat.id === active.id
          ? {
              ...chat,
              title: chat.messages.length === 0 ? question : chat.title,
              messages: chat.messages.concat(
                { id: `${Date.now()}-q`, role: "user", text: question },
                { id: `${Date.now()}-a`, role: "assistant", text: reply.text, action: reply.action }
              ),
            }
          : chat
      )
    );
    setDraft("");
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 rounded-full bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-lg"
      >
        Ask
      </button>
    );
  }

  return (
    <div className="fixed inset-y-4 right-4 z-40 flex w-[min(920px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
      <aside className="flex w-64 shrink-0 flex-col border-r border-gray-100">
        <div className="p-3">
          <button
            type="button"
            onClick={() => startChat()}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800"
          >
            + New chat
          </button>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search chats"
            className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-3">
          {visibleChats.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-gray-400">No conversations yet.</p>
          ) : (
            visibleChats.map((chat) => (
              <button
                key={chat.id}
                type="button"
                onClick={() => setActiveId(chat.id)}
                className={`mb-1 w-full truncate rounded-lg px-3 py-2 text-left text-sm ${
                  chat.id === activeId ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {chat.title}
              </button>
            ))
          )}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <div>
            <p className="font-semibold text-gray-900">Ask</p>
            <p className="text-xs text-gray-500">Help for this account</p>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="px-2 text-xl text-gray-400" aria-label="Close help">
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {!active || active.messages.length === 0 ? (
            <div className="mx-auto max-w-xl pt-10 text-center">
              <h2 className="text-2xl font-semibold text-gray-900">How can I help you today?</h2>
              <p className="mt-2 text-sm text-gray-500">
                Ask about your account, leads, sequences, or WhatsApp setup — or start with one of these.
              </p>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => startChat(prompt)}
                    className="rounded-xl border border-gray-200 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {active.messages.map((message) => (
                <div key={message.id} className={message.role === "user" ? "text-right" : "text-left"}>
                  <div
                    className={`inline-block max-w-[85%] rounded-2xl px-4 py-2 text-left text-sm ${
                      message.role === "user" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    <p>{message.text}</p>
                    {message.action ? (
                      <button
                        type="button"
                        className="mt-2 text-sm font-medium text-blue-700"
                        onClick={() => navigate(message.action!.path)}
                      >
                        {message.action.label}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form
          className="border-t border-gray-100 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            send(draft);
          }}
        >
          <div className="flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2">
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask anything..."
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
            <button type="submit" className="rounded-full bg-blue-600 px-3 py-1 text-sm text-white" aria-label="Send">
              →
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-gray-400">
            Answers describe this account. Check the page before you change a setting.
          </p>
        </form>
      </section>
    </div>
  );
}
